/**
 * NetworkClubFinder Component
 * Zoek en ontdek netwerkclubs op basis van regio, provincie of categorie
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Search, MapPin, Users, Calendar, ExternalLink, ChevronDown, X, CheckCircle, Building2, Globe, Plus, Send, Info } from 'lucide-react';
import { networkClubService } from '../services/networkClubService';
import { 
  NetworkClub, 
  DUTCH_PROVINCES, 
  CLUB_CATEGORIES, 
  ClubScope,
  SCOPE_OPTIONS,
  REGION_GROUPS,
  AVAILABLE_COUNTRIES 
} from '../types/networkclub';
import { auth, db } from '../firebase';
import { collection, addDoc, doc, setDoc, serverTimestamp } from 'firebase/firestore';

interface NetworkClubFinderProps {
  isOpen: boolean;
  onClose: () => void;
}

const NetworkClubFinder: React.FC<NetworkClubFinderProps> = ({ isOpen, onClose }) => {
  const [clubs, setClubs] = useState<NetworkClub[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showRegionDropdown, setShowRegionDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [regionCounts, setRegionCounts] = useState<Map<string, number>>(new Map());
  const [selectedClub, setSelectedClub] = useState<NetworkClub | null>(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);

  // Load clubs
  useEffect(() => {
    if (!isOpen) return;

    const loadClubs = async () => {
      setLoading(true);
      try {
        const [fetchedClubs, regions] = await Promise.all([
          networkClubService.getNetworkClubs(),
          networkClubService.getRegionsWithCounts()
        ]);
        setClubs(fetchedClubs);
        
        const counts = new Map<string, number>();
        regions.forEach(r => counts.set(r.region, r.count));
        setRegionCounts(counts);
      } catch (error) {
        console.error('Error loading clubs:', error);
      } finally {
        setLoading(false);
      }
    };

    loadClubs();
  }, [isOpen]);

  // Filter clubs
  const filteredClubs = useMemo(() => {
    let result = [...clubs];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(club =>
        club.name.toLowerCase().includes(term) ||
        club.description?.toLowerCase().includes(term) ||
        club.city?.toLowerCase().includes(term)
      );
    }

    if (selectedRegion) {
      result = result.filter(club => club.region === selectedRegion);
    }

    if (selectedCategory) {
      result = result.filter(club => club.categories?.includes(selectedCategory));
    }

    return result;
  }, [clubs, searchTerm, selectedRegion, selectedCategory]);

  // Stats
  const stats = useMemo(() => ({
    totalClubs: clubs.length,
    totalMembers: clubs.reduce((sum, club) => sum + (club.memberCount || 0), 0),
    activeRegions: new Set(clubs.map(c => c.region)).size
  }), [clubs]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedRegion('');
    setSelectedCategory('');
  };

  const hasActiveFilters = searchTerm || selectedRegion || selectedCategory;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#7A9488] to-[#9AAF88] p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Netwerkclub Finder</h2>
                <p className="text-white/80 text-sm">Vind & connect met jouw netwerk</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowRegisterModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Club aanmelden
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="flex gap-6 text-sm">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-white/70" />
              <span><strong>{stats.totalClubs}</strong> clubs</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-white/70" />
              <span><strong>{stats.totalMembers.toLocaleString()}</strong> leden</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-white/70" />
              <span><strong>{stats.activeRegions}</strong> provincies</span>
            </div>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="p-4 border-b bg-gray-50">
          <div className="flex flex-wrap gap-3">
            {/* Search Input */}
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Zoek op naam, stad of beschrijving..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#7A9488] focus:border-transparent"
              />
            </div>

            {/* Region Filter */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowRegionDropdown(!showRegionDropdown);
                  setShowCategoryDropdown(false);
                }}
                className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl transition-colors ${
                  selectedRegion 
                    ? 'bg-stone-50 border-stone-300 text-[#4F6660]' 
                    : 'border-gray-200 hover:bg-gray-100'
                }`}
              >
                <MapPin className="w-4 h-4" />
                <span>{selectedRegion || 'Provincie'}</span>
                <ChevronDown className="w-4 h-4" />
              </button>

              {showRegionDropdown && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-10 max-h-80 overflow-y-auto">
                  <button
                    onClick={() => {
                      setSelectedRegion('');
                      setShowRegionDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 text-gray-500"
                  >
                    Alle provincies
                  </button>
                  <div className="border-t border-gray-100 my-1"></div>
                  {DUTCH_PROVINCES.map(province => (
                    <button
                      key={province}
                      onClick={() => {
                        setSelectedRegion(province);
                        setShowRegionDropdown(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between ${
                        selectedRegion === province ? 'bg-stone-50 text-[#4F6660]' : ''
                      }`}
                    >
                      <span>{province}</span>
                      <span className="text-gray-400 text-xs">
                        {regionCounts.get(province) || 0} clubs
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Category Filter */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowCategoryDropdown(!showCategoryDropdown);
                  setShowRegionDropdown(false);
                }}
                className={`flex items-center gap-2 px-4 py-2.5 border rounded-xl transition-colors ${
                  selectedCategory 
                    ? 'bg-stone-50 border-stone-300 text-[#4F6660]' 
                    : 'border-gray-200 hover:bg-gray-100'
                }`}
              >
                <Globe className="w-4 h-4" />
                <span className="max-w-[120px] truncate">{selectedCategory || 'Categorie'}</span>
                <ChevronDown className="w-4 h-4" />
              </button>

              {showCategoryDropdown && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-10 max-h-80 overflow-y-auto">
                  <button
                    onClick={() => {
                      setSelectedCategory('');
                      setShowCategoryDropdown(false);
                    }}
                    className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 text-gray-500"
                  >
                    Alle categorieën
                  </button>
                  <div className="border-t border-gray-100 my-1"></div>
                  {CLUB_CATEGORIES.map(category => (
                    <button
                      key={category}
                      onClick={() => {
                        setSelectedCategory(category);
                        setShowCategoryDropdown(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                        selectedCategory === category ? 'bg-stone-50 text-[#4F6660]' : ''
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-2 px-4 py-2.5 text-gray-500 hover:text-gray-700 transition-colors"
              >
                <X className="w-4 h-4" />
                <span>Wissen</span>
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-3 border-[#7A9488] border-t-transparent rounded-full animate-spin"></div>
                <p className="text-gray-500">Clubs laden...</p>
              </div>
            </div>
          ) : filteredClubs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">Geen clubs gevonden</h3>
              <p className="text-gray-500 max-w-sm">
                {hasActiveFilters 
                  ? 'Probeer andere zoektermen of filters'
                  : 'Er zijn nog geen netwerkclubs beschikbaar'}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-4 text-[#4F6660] hover:text-[#4F6660] font-medium"
                >
                  Filters wissen
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredClubs.map(club => (
                <ClubCard 
                  key={club.id} 
                  club={club} 
                  onClick={() => setSelectedClub(club)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Results Footer */}
        {!loading && filteredClubs.length > 0 && (
          <div className="p-4 border-t bg-gray-50 text-center text-sm text-gray-500">
            {filteredClubs.length} {filteredClubs.length === 1 ? 'club' : 'clubs'} gevonden
            {hasActiveFilters && ` (van ${clubs.length} totaal)`}
          </div>
        )}
      </div>

      {/* Club Detail Modal */}
      {selectedClub && (
        <ClubDetailModal 
          club={selectedClub} 
          onClose={() => setSelectedClub(null)} 
        />
      )}

      {/* Register Club Modal */}
      {showRegisterModal && (
        <RegisterClubModal 
          onClose={() => setShowRegisterModal(false)}
          onSuccess={() => {
            setShowRegisterModal(false);
            // Optionally reload clubs after registration
          }}
        />
      )}
    </div>
  );
};

// Club Card Component
const ClubCard: React.FC<{ club: NetworkClub; onClick: () => void }> = ({ club, onClick }) => {
  // Generate gradient based on club name
  const gradients = [
    'from-[#7A9488] to-[#9AAF88]',
    'from-blue-400 to-indigo-500',
    'from-emerald-400 to-teal-500',
    'from-purple-400 to-pink-500',
    'from-rose-400 to-[#7A9488]',
    'from-cyan-400 to-blue-500'
  ];
  const gradientIndex = club.name.length % gradients.length;
  const gradient = gradients[gradientIndex];

  return (
    <button
      onClick={onClick}
      className="bg-white rounded-xl border border-gray-200 hover:border-stone-300 hover:shadow-lg transition-all p-4 text-left group"
    >
      <div className="flex gap-4">
        {/* Logo/Avatar */}
        <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`}>
          {club.logoUrl ? (
            <img src={club.logoUrl} alt="" className="w-full h-full rounded-xl object-cover" />
          ) : (
            <span className="text-white text-xl font-bold">
              {club.name.substring(0, 2).toUpperCase()}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h3 className="font-semibold text-gray-900 group-hover:text-[#4F6660] transition-colors truncate">
              {club.name}
            </h3>
            {club.isVerified && (
              <CheckCircle className="w-5 h-5 text-blue-500 flex-shrink-0" />
            )}
          </div>

          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <MapPin className="w-3.5 h-3.5" />
              {club.city || club.region}
            </span>
            {club.memberCount && (
              <span className="flex items-center gap-1">
                <Users className="w-3.5 h-3.5" />
                {club.memberCount}
              </span>
            )}
          </div>

          {club.description && (
            <p className="text-sm text-gray-600 mt-2 line-clamp-2">
              {club.description}
            </p>
          )}

          {/* Categories */}
          {club.categories && club.categories.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {club.categories.slice(0, 3).map(cat => (
                <span 
                  key={cat}
                  className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full"
                >
                  {cat}
                </span>
              ))}
              {club.categories.length > 3 && (
                <span className="px-2 py-0.5 text-gray-400 text-xs">
                  +{club.categories.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

// Club Detail Modal
const ClubDetailModal: React.FC<{ club: NetworkClub; onClose: () => void }> = ({ club, onClose }) => {
  const gradients = [
    'from-[#7A9488] to-[#9AAF88]',
    'from-blue-400 to-indigo-500',
    'from-emerald-400 to-teal-500',
    'from-purple-400 to-pink-500',
    'from-rose-400 to-[#7A9488]',
    'from-cyan-400 to-blue-500'
  ];
  const gradientIndex = club.name.length % gradients.length;
  const gradient = gradients[gradientIndex];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className={`bg-gradient-to-br ${gradient} p-6 text-white`}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                {club.logoUrl ? (
                  <img src={club.logoUrl} alt="" className="w-full h-full rounded-xl object-cover" />
                ) : (
                  <span className="text-2xl font-bold">
                    {club.name.substring(0, 2).toUpperCase()}
                  </span>
                )}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold">{club.name}</h2>
                  {club.isVerified && (
                    <CheckCircle className="w-5 h-5 text-white/90" />
                  )}
                </div>
                <p className="text-white/80 flex items-center gap-1 mt-1">
                  <MapPin className="w-4 h-4" />
                  {club.city}, {club.region}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {club.memberCount && (
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{club.memberCount}</div>
                <div className="text-sm text-gray-500">Leden</div>
              </div>
            )}
            {club.foundedYear && (
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{club.foundedYear}</div>
                <div className="text-sm text-gray-500">Opgericht</div>
              </div>
            )}
            {club.meetingFrequency && (
              <div className="text-center">
                <div className="text-lg font-bold text-gray-900">{club.meetingFrequency}</div>
                <div className="text-sm text-gray-500">Meetings</div>
              </div>
            )}
          </div>

          {/* Description */}
          {club.description && (
            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-2">Over deze club</h3>
              <p className="text-gray-600">{club.description}</p>
            </div>
          )}

          {/* Categories */}
          {club.categories && club.categories.length > 0 && (
            <div className="mb-6">
              <h3 className="font-medium text-gray-900 mb-2">Categorieën</h3>
              <div className="flex flex-wrap gap-2">
                {club.categories.map(cat => (
                  <span 
                    key={cat}
                    className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                  >
                    {cat}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            {club.website && (
              <a
                href={club.website}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#7A9488] hover:bg-[#4F6660] text-white font-medium rounded-xl transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Bezoek website
              </a>
            )}
            <button
              onClick={() => {
                // TODO: Implement connection request
                alert('Contact functionaliteit komt binnenkort!');
              }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium rounded-xl transition-colors"
            >
              <Users className="w-4 h-4" />
              Contact opnemen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Register Club Modal
const RegisterClubModal: React.FC<{ onClose: () => void; onSuccess: () => void }> = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    kvkNumber: '',
    scope: 'lokaal' as ClubScope,
    region: '',
    city: '',
    activeRegions: [] as string[],
    activeCountries: ['Nederland'] as string[],
    website: '',
    email: '',
    categories: [] as string[],
    meetingFrequency: '',
    // ✅ NIEUW: Type & Visibility
    type: 'networkclub' as 'networkclub' | 'mastermind' | 'community',
    visibility: 'public' as 'public' | 'private'
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [showRegionGroups, setShowRegionGroups] = useState(false);

  const handleCategoryToggle = (category: string) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(category)
        ? prev.categories.filter(c => c !== category)
        : [...prev.categories, category].slice(0, 3) // Max 3 categories
    }));
  };

  const handleRegionToggle = (region: string) => {
    setFormData(prev => ({
      ...prev,
      activeRegions: prev.activeRegions.includes(region)
        ? prev.activeRegions.filter(r => r !== region)
        : [...prev.activeRegions, region]
    }));
  };

  const handleCountryToggle = (country: string) => {
    setFormData(prev => ({
      ...prev,
      activeCountries: prev.activeCountries.includes(country)
        ? prev.activeCountries.filter(c => c !== country)
        : [...prev.activeCountries, country]
    }));
  };

  const handleRegionGroupSelect = (groupName: string) => {
    const groupRegions = REGION_GROUPS[groupName] || [];
    setFormData(prev => ({
      ...prev,
      activeRegions: Array.from(new Set([...prev.activeRegions, ...groupRegions]))
    }));
  };

  const handleScopeChange = (newScope: ClubScope) => {
    setFormData(prev => ({
      ...prev,
      scope: newScope,
      // Reset region selections when scope changes
      activeRegions: newScope === 'lokaal' ? (prev.region ? [prev.region] : []) : prev.activeRegions,
      activeCountries: newScope === 'internationaal' ? prev.activeCountries : ['Nederland']
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError('Naam is verplicht');
      return;
    }
    
    // Validate region based on scope
    if (formData.scope === 'lokaal' && !formData.region) {
      setError('Selecteer een provincie voor lokale clubs');
      return;
    }
    if (formData.scope === 'regionaal' && formData.activeRegions.length === 0) {
      setError('Selecteer minimaal één provincie voor regionale clubs');
      return;
    }
    if (formData.scope === 'internationaal' && formData.activeCountries.length === 0) {
      setError('Selecteer minimaal één land voor internationale clubs');
      return;
    }
    
    if (formData.kvkNumber && formData.kvkNumber.length !== 8) {
      setError('KVK nummer moet 8 cijfers zijn');
      return;
    }

    setLoading(true);

    try {
      const user = auth.currentUser;
      
      // Determine primary region
      const primaryRegion = formData.scope === 'lokaal' 
        ? formData.region 
        : formData.activeRegions[0] || formData.region || 'Nederland';
      
      // Build activeRegions based on scope
      let activeRegions: string[] = [];
      if (formData.scope === 'lokaal') {
        activeRegions = formData.region ? [formData.region] : [];
      } else if (formData.scope === 'regionaal') {
        activeRegions = formData.activeRegions;
      } else if (formData.scope === 'nationaal') {
        activeRegions = [...DUTCH_PROVINCES]; // All provinces
      }
      
      const clubData: Omit<NetworkClub, 'id'> = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        kvkNumber: formData.kvkNumber || undefined,
        scope: formData.scope,
        region: primaryRegion,
        activeRegions: activeRegions.length > 0 ? activeRegions : undefined,
        activeCountries: formData.scope === 'internationaal' ? formData.activeCountries : undefined,
        city: formData.city.trim() || undefined,
        country: formData.scope === 'internationaal' ? formData.activeCountries[0] || 'Nederland' : 'Nederland',
        website: formData.website.trim() || undefined,
        email: formData.email.trim() || user?.email || undefined,
        categories: formData.categories.length > 0 ? formData.categories : undefined,
        meetingFrequency: formData.meetingFrequency || undefined,
        moderationStatus: 'pending',
        organizerId: user?.uid,
        organizerName: user?.displayName || undefined,
        organizerEmail: user?.email || undefined,
        isVerified: false,
        isKvkValidated: false,
        // ✅ NIEUW: Visibility & Type
        type: formData.type,
        visibility: formData.visibility,
        isActive: false,                    // Niet actief tot 5 leden
        isVisible: false,                   // Niet zichtbaar tot 5 leden
        memberCount: 1,                    // Organizer telt als 1
        minMembersRequired: 5
      };

      // 1. Maak networkClub aan
      const clubId = await networkClubService.addNetworkClub(clubData);
      
      if (!clubId) {
        setError('Kon club niet aanmelden. Probeer opnieuw.');
        setLoading(false);
        return;
      }

      // 2. ✅ NIEUW: Maak entity aan
      let entityId: string | null = null;
      try {
        const entityData: any = {
          type: formData.type,
          name: formData.name.trim(),
          ownerId: user?.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          networkClubId: clubId  // Reverse link
        };

        // Type-specifieke velden
        if (formData.type === 'networkclub' && formData.kvkNumber) {
          entityData.kvkNumber = formData.kvkNumber;
          entityData.isKvkValidated = false;
        }

        const entityRef = await addDoc(collection(db, 'entities'), entityData);
        entityId = entityRef.id;

        // 3. ✅ NIEUW: Maak entityMember aan (organizer rol)
        await setDoc(doc(db, 'entityMembers', `${entityId}_${user?.uid}`), {
          entityId: entityId,
          userId: user?.uid,
          role: formData.type === 'networkclub' ? 'organizer' : 'owner',
          createdAt: serverTimestamp(),
          createdBy: user?.uid,
        });

        // 4. ✅ NIEUW: Update networkClub met entityId
        await networkClubService.updateNetworkClub(clubId, {
          entityId: entityId
        });

        setSubmitted(true);
        setTimeout(() => {
          onSuccess();
        }, 2000);
      } catch (err: any) {
        console.error('Error creating entity:', err);
        setError('Club aangemaakt, maar entity koppeling mislukt. Probeer later opnieuw.');
        setLoading(false);
      }
    } catch (err) {
      console.error('Error registering club:', err);
      setError('Er ging iets mis. Probeer opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Aanvraag verzonden!</h3>
          <p className="text-gray-600">
            Je aanvraag wordt beoordeeld. Je ontvangt bericht zodra je club is goedgekeurd.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#7A9488] to-[#9AAF88] p-5 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <Plus className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Club aanmelden</h2>
                <p className="text-white/80 text-sm">Registreer je netwerkclub</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          {/* ✅ NIEUW: Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-3 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'networkclub' })}
                className={`p-4 border-2 rounded-lg transition-colors ${
                  formData.type === 'networkclub'
                    ? 'border-[#7A9488] bg-stone-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-2xl mb-2">🏢</div>
                <div className="font-medium text-sm">Netwerkclub</div>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'mastermind' })}
                className={`p-4 border-2 rounded-lg transition-colors ${
                  formData.type === 'mastermind'
                    ? 'border-[#7A9488] bg-stone-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-2xl mb-2">🧠</div>
                <div className="font-medium text-sm">Mastermind</div>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, type: 'community' })}
                className={`p-4 border-2 rounded-lg transition-colors ${
                  formData.type === 'community'
                    ? 'border-[#7A9488] bg-stone-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-2xl mb-2">🌍</div>
                <div className="font-medium text-sm">Community</div>
              </button>
            </div>
          </div>

          {/* ✅ NIEUW: Visibility Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Zichtbaarheid <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, visibility: 'public' })}
                className={`p-4 border-2 rounded-lg transition-colors text-left ${
                  formData.visibility === 'public'
                    ? 'border-[#7A9488] bg-stone-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium mb-1">Open</div>
                <div className="text-xs text-gray-600">
                  Iedereen kan de club vinden en lid worden
                </div>
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, visibility: 'private' })}
                className={`p-4 border-2 rounded-lg transition-colors text-left ${
                  formData.visibility === 'private'
                    ? 'border-[#7A9488] bg-stone-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="font-medium mb-1">Besloten</div>
                <div className="text-xs text-gray-600">
                  Alleen uitgenodigde leden kunnen deelnemen
                </div>
              </button>
            </div>
          </div>

          {/* ✅ NIEUW: Info over minimum leden */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start space-x-2">
              <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-sm font-medium text-blue-900 mb-1">
                  Club wordt pas actief bij 5 leden
                </p>
                <p className="text-xs text-blue-700">
                  Je club kan worden aangemaakt, maar is pas actief en zichtbaar in de 
                  zoekfunctie en carrousel wanneer minimaal 5 leden zich hebben aangemeld.
                </p>
              </div>
            </div>
          </div>

          {/* Club Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Naam club <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Bijv. Amsterdam Entrepreneurs Network"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A9488]"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Beschrijving
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Korte beschrijving van je club..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A9488] resize-none"
            />
          </div>

          {/* Scope Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Werkgebied van de club <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {SCOPE_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleScopeChange(option.value)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    formData.scope === option.value
                      ? 'border-[#7A9488] bg-stone-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">
                      {option.value === 'lokaal' && '🏠'}
                      {option.value === 'regionaal' && '📍'}
                      {option.value === 'nationaal' && '🇳🇱'}
                      {option.value === 'internationaal' && '🌍'}
                    </span>
                    <span className={`font-medium ${formData.scope === option.value ? 'text-[#4F6660]' : 'text-gray-900'}`}>
                      {option.label}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{option.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Lokaal: Single Province + City */}
          {formData.scope === 'lokaal' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Provincie <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.region}
                  onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A9488]"
                  required
                >
                  <option value="">Selecteer...</option>
                  {DUTCH_PROVINCES.map(province => (
                    <option key={province} value={province}>{province}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Stad
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Bijv. Amsterdam"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A9488]"
                />
              </div>
            </div>
          )}

          {/* Regionaal: Multi Province Selection */}
          {formData.scope === 'regionaal' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Actieve provincies <span className="text-red-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowRegionGroups(!showRegionGroups)}
                  className="text-xs text-[#4F6660] hover:text-[#4F6660]"
                >
                  {showRegionGroups ? 'Verberg groepen' : 'Snelle selectie'}
                </button>
              </div>
              
              {/* Region Groups */}
              {showRegionGroups && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {Object.keys(REGION_GROUPS).map(groupName => (
                    <button
                      key={groupName}
                      type="button"
                      onClick={() => handleRegionGroupSelect(groupName)}
                      className="px-3 py-1.5 text-xs bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 transition-colors"
                    >
                      + {groupName}
                    </button>
                  ))}
                </div>
              )}
              
              {/* Province checkboxes */}
              <div className="grid grid-cols-3 gap-2 p-3 bg-gray-50 rounded-lg max-h-48 overflow-y-auto">
                {DUTCH_PROVINCES.map(province => (
                  <label 
                    key={province}
                    className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={formData.activeRegions.includes(province)}
                      onChange={() => handleRegionToggle(province)}
                      className="w-4 h-4 text-[#7A9488] border-gray-300 rounded focus:ring-[#7A9488]"
                    />
                    <span className="text-sm text-gray-700">{province}</span>
                  </label>
                ))}
              </div>
              
              {/* Selected count */}
              {formData.activeRegions.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  {formData.activeRegions.length} provincie{formData.activeRegions.length !== 1 && 's'} geselecteerd
                </p>
              )}
            </div>
          )}

          {/* Nationaal: Info message */}
          {formData.scope === 'nationaal' && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-2">
                <span className="text-blue-500">🇳🇱</span>
                <div>
                  <p className="text-sm font-medium text-blue-800">Nationale dekking</p>
                  <p className="text-xs text-blue-600 mt-1">
                    Je club wordt zichtbaar voor alle ondernemers in Nederland
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Internationaal: Country Selection */}
          {formData.scope === 'internationaal' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Actieve landen <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 gap-2 p-3 bg-gray-50 rounded-lg max-h-48 overflow-y-auto">
                {AVAILABLE_COUNTRIES.map(country => (
                  <label 
                    key={country}
                    className="flex items-center gap-2 cursor-pointer hover:bg-white p-1 rounded"
                  >
                    <input
                      type="checkbox"
                      checked={formData.activeCountries.includes(country)}
                      onChange={() => handleCountryToggle(country)}
                      className="w-4 h-4 text-[#7A9488] border-gray-300 rounded focus:ring-[#7A9488]"
                    />
                    <span className="text-sm text-gray-700">{country}</span>
                  </label>
                ))}
              </div>
              
              {/* Selected count */}
              {formData.activeCountries.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  {formData.activeCountries.length} {formData.activeCountries.length === 1 ? 'land' : 'landen'} geselecteerd
                </p>
              )}
            </div>
          )}

          {/* KVK Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              KVK nummer (optioneel)
            </label>
            <input
              type="text"
              value={formData.kvkNumber}
              onChange={(e) => setFormData({ ...formData, kvkNumber: e.target.value.replace(/\D/g, '').slice(0, 8) })}
              placeholder="12345678"
              maxLength={8}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A9488]"
            />
            <p className="text-xs text-gray-500 mt-1">8 cijfers. Snellere goedkeuring met KVK.</p>
          </div>

          {/* Website & Email */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Website
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A9488]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contact email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="info@club.nl"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A9488]"
              />
            </div>
          </div>

          {/* Categories */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Categorieën (max 3)
            </label>
            <div className="flex flex-wrap gap-2">
              {CLUB_CATEGORIES.slice(0, 12).map(category => (
                <button
                  key={category}
                  type="button"
                  onClick={() => handleCategoryToggle(category)}
                  className={`px-3 py-1.5 text-sm rounded-full transition-colors ${
                    formData.categories.includes(category)
                      ? 'bg-[#7A9488] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Meeting Frequency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Meeting frequentie
            </label>
            <select
              value={formData.meetingFrequency}
              onChange={(e) => setFormData({ ...formData, meetingFrequency: e.target.value })}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7A9488]"
            >
              <option value="">Selecteer...</option>
              <option value="Wekelijks">Wekelijks</option>
              <option value="Tweewekelijks">Tweewekelijks</option>
              <option value="Maandelijks">Maandelijks</option>
              <option value="Kwartaal">Per kwartaal</option>
              <option value="Op aanvraag">Op aanvraag</option>
            </select>
          </div>
        </form>

        {/* Footer */}
        <div className="p-5 border-t bg-gray-50 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Annuleren
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#7A9488] hover:bg-[#4F6660] text-white rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? (
              'Verzenden...'
            ) : (
              <>
                <Send className="w-4 h-4" />
                Aanmelden
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NetworkClubFinder;
